import { useState, useRef, useEffect, useCallback, Suspense, forwardRef, useImperativeHandle } from 'react'
import { Canvas, useThree } from '@react-three/fiber'
import { OrbitControls } from '@react-three/drei'
import * as THREE from 'three'
import { Model, type ModelObject } from '../model/Model'
import { TextureOperationPanel, type TextureOperationPanelRef } from './components/TextureOperationPanel'
import { getNativeData, isInNativeApp, postMessageToNative } from '../../services/nativeBridge'

'http://192.168.1.138:3000/custom-deck-cover?fromType=mall&token=eyJhbGciOiJIUzUxMiJ9.eyJzdWIiOiJ7XCJpZFwiOjE3NTMsXCJsb2dpbk5hbWVcIjpcIjEyMzMwOVwiLFwicm9sZXNcIjpcInVzZXIsc3VwZXIsYWRtaW4sc3ViY3NcIn0iLCJleHAiOjE4Mzc4NjI1Mzh9.T4ELmgVx8H5WYrQXf-HvTooeUnx5HUiMdKivetoxQ57sGxvP77JDOcUo2hCipm6_6FIR9DTnmAGA2SwROAL1jg'



export function CustomDeckCoverPage() {

  const [color, setColor] = useState('#ffffff')
  const [backgroundColor, setBackgroundColor] = useState('#16213e')
  const [texture, setTexture] = useState<string | undefined>(undefined)
  const [modelObjects, setModelObjects] = useState<ModelObject[]>([])
  const [selectedObjectId, setSelectedObjectId] = useState<string | undefined>(undefined)
  const [applyTextureToSelected, setApplyTextureToSelected] = useState(false)
  const [pendingTexture, setPendingTexture] = useState<string | undefined>(undefined)
  const [textureAdjust] = useState({ offset: [0, 0] as [number, number], rotation: 0, scale: 1 })

  const modelObjectsRef = useRef<ModelObject[]>([])
  const texturePanelRef = useRef<TextureOperationPanelRef>(null)

  // 1. 查找目标 Mesh 对象 (如 Deck/Board)
  const getTextureModelObject = useCallback((objects: ModelObject[]) => {
    if (!objects.length) return null
    return objects.find(obj =>
      ['deck', 'board', 'mat'].some(key => obj.name.toLowerCase().includes(key))
    ) || objects[0]
  }, [])

  // 2. 自动应用纹理与默认选中
  useEffect(() => {
    modelObjectsRef.current = modelObjects
    const deck = getTextureModelObject(modelObjects)
    if (deck) {
      if (!selectedObjectId) setSelectedObjectId(deck.id)
      if (pendingTexture) {
        setTexture(pendingTexture)
        setApplyTextureToSelected(true)
        setPendingTexture(undefined)
      }
    }
  }, [modelObjects, selectedObjectId, pendingTexture, getTextureModelObject])

  const _changeTexture = useCallback((newTexture: string) => {
    // console.log('Applying texture:', newTexture)
    if (modelObjectsRef.current.length === 0) {
      setPendingTexture(newTexture)
    } else {
      setTexture(newTexture)
      setApplyTextureToSelected(true)
    }
  }, [])

  // 3. 处理原生桥接消息
  const handleMessage = useCallback((event: any) => {
    const { type, data } = event.detail
    switch (type) {
      case 'changeTexture': _changeTexture(data); break
      case 'submit': texturePanelRef.current?.submit(); break
      case 'viewMyModels': texturePanelRef.current?.showList(); break
      default: console.log('Unhandled native message:', type); break
    }
  }, [_changeTexture])

  useEffect(() => {
    window.addEventListener('nativeMessage', handleMessage)
    return () => window.removeEventListener('nativeMessage', handleMessage)
  }, [handleMessage])

  // 4. 初始化数据与菜单
  useEffect(() => {
    const init = async () => {
      try {
        const nativeData = getNativeData()
        if (nativeData.imageUrl) {
          setTexture(nativeData.imageUrl)
          setApplyTextureToSelected(true)
        }

        if (isInNativeApp() && nativeData.xToken) {
          postMessageToNative('updateMenus', [
            { title: '我的列表', postData: { type: 'viewMyModels', data: {} } },
            { title: '  提交  ', postData: { type: 'submit', data: {} } },
          ])
        }
      } catch (e) {
        console.error('Init failed:', e)
      }
    }
    init()
    return () => postMessageToNative('updateMenus', [])
  }, [])

  // 5. 事件处理
  const handleObjectsExtracted = useCallback((objects: ModelObject[]) => setModelObjects(objects), [])

  const handleObjectColorUpdated = useCallback((id: string, newColor: string) => {
    setModelObjects(prev => prev.map(o => o.id === id ? { ...o, color: newColor } : o))
  }, [])

  const handleObjectTextureUpdated = useCallback((id: string, newTexture: string) => {
    setModelObjects(prev => prev.map(o => o.id === id ? { ...o, texture: newTexture } : o))
    setApplyTextureToSelected(false)
  }, [])

  const captureRef = useRef<{ capture: () => Promise<string> }>(null)

  const getModelCoverImage = useCallback(async () => {
    if (captureRef.current) {
      return await captureRef.current.capture()
    }
    return ''
  }, [])

  // 6. UI 渲染
  const renderTopBar = () => (
    <div className="px-4 py-3 flex justify-between items-center bg-black/30 text-white shrink-0 flex-wrap gap-4 z-10">
      <div className="flex items-center gap-5 flex-wrap">
        <ColorPicker label="模型颜色" value={color} onChange={setColor} />
        <ColorPicker label="背景颜色" value={backgroundColor} onChange={setBackgroundColor} />
      </div>
    </div>
  )

  return (
    <div className="w-full h-full min-h-svh flex flex-col bg-linear-to-br from-[#1a1a2e] to-[#16213e] relative overflow-hidden">
      {renderTopBar()}
      <div className="flex-1 flex flex-col md:flex-row overflow-hidden">
        <div className="flex-1 relative w-full h-full overflow-hidden" style={{ backgroundColor }}>
          <Canvas camera={{ position: [0, 0, 6], fov: 50 }} gl={{ antialias: true, alpha: true, preserveDrawingBuffer: true }}>
            <color attach="background" args={[backgroundColor]} />
            <Suspense fallback={null}>
              <Model
                color={color}
                texture={texture}
                selectedObjectId={selectedObjectId}
                onObjectsExtracted={handleObjectsExtracted}
                onObjectColorUpdated={handleObjectColorUpdated}
                onObjectTextureUpdated={handleObjectTextureUpdated}
                applyTextureToSelected={applyTextureToSelected}
                textureAdjust={textureAdjust}
              />
              <CaptureHelper ref={captureRef} backgroundColor={backgroundColor} />
            </Suspense>
            <OrbitControls makeDefault />
          </Canvas>
        </div>
        <TextureOperationPanel ref={texturePanelRef} texture={texture} handleMessage={handleMessage} getModelCoverImage={getModelCoverImage} />
      </div>
    </div>
  )
}

// 辅助组件：颜色选择器
function ColorPicker({ label, value, onChange }: { label: string, value: string, onChange: (v: string) => void }) {
  return (
    <div className="flex items-center gap-2">
      <label className="text-xs">{label}：</label>
      <input
        type="color"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-[30px] h-[25px] border-0 rounded cursor-pointer p-0"
      />
      <span className="font-mono text-xs uppercase">{value}</span>
    </div>
  )
}

// 辅助组件：模型正面图片截图
const CaptureHelper = forwardRef<{ capture: () => Promise<string> }, { backgroundColor: string }>((props, ref) => {
  const { gl, scene, camera } = useThree()

  useImperativeHandle(ref, () => ({
    capture: async () => {
      // 1. 设置高质量正方形尺寸
      const size = 1024
      const renderTarget = new THREE.WebGLRenderTarget(size, size, {
        samples: 8, // 启用多重采样抗锯齿 (MSAA)
        colorSpace: THREE.SRGBColorSpace, // 确保色彩空间一致，避免“蒙版”感（色彩偏淡）
        format: THREE.RGBAFormat,
        type: THREE.UnsignedByteType,
        minFilter: THREE.LinearFilter,
        magFilter: THREE.LinearFilter,
        generateMipmaps: false,
      })

      // 2. 保存当前状态
      const oldPosition = camera.position.clone()
      const oldRotation = camera.rotation.clone()
      const oldAspect = (camera as THREE.PerspectiveCamera).aspect
      const oldBackground = scene.background
      const oldClearColor = new THREE.Color()
      gl.getClearColor(oldClearColor)
      const oldClearAlpha = gl.getClearAlpha()

      // 3. 设置截图专用状态
      // a. 设置正面视角
      camera.position.set(0, 0, 6)
      camera.lookAt(0, 0, 0)
      if (camera instanceof THREE.PerspectiveCamera) {
        camera.aspect = 1
        camera.updateProjectionMatrix()
      }

      // b. 设置背景色
      const bgColor = new THREE.Color(props.backgroundColor)
      scene.background = bgColor

      // c. 临时增加光照强度 (解决生成的图片偏暗的问题)
      // 注意：如果启用了 SRGBColorSpace，可能不再需要手动翻倍光照
      const lights: { light: THREE.Light, intensity: number }[] = []
      scene.traverse((obj) => {
        if (obj instanceof THREE.Light) {
          lights.push({ light: obj, intensity: obj.intensity })
          obj.intensity *= 1.5 // 稍微提高亮度即可，SRGB 空间下 2 倍可能过亮
        }
      })

      // 4. 渲染到离屏渲染目标
      gl.setRenderTarget(renderTarget)
      gl.setClearColor(bgColor, 1)
      gl.clear()
      gl.render(scene, camera)
      gl.setRenderTarget(null)

      // 5. 读取像素并转换为 DataURL (需要翻转 Y 轴)
      const buffer = new Uint8Array(size * size * 4)
      gl.readRenderTargetPixels(renderTarget, 0, 0, size, size, buffer)

      const tempCanvas = document.createElement('canvas')
      tempCanvas.width = size
      tempCanvas.height = size
      const ctx = tempCanvas.getContext('2d')
      if (ctx) {
        const imageData = ctx.createImageData(size, size)
        // 使用更高效的方式翻转像素
        for (let y = 0; y < size; y++) {
          const sourceY = size - 1 - y
          const destOffset = y * size * 4
          const sourceOffset = sourceY * size * 4
          imageData.data.set(buffer.subarray(sourceOffset, sourceOffset + size * 4), destOffset)
        }
        ctx.putImageData(imageData, 0, 0)
      }
      const dataUrl = tempCanvas.toDataURL('image/png', 1.0) // 使用最高质量

      // 还原状态并释放资源
      camera.position.copy(oldPosition)
      camera.rotation.copy(oldRotation)
      if (camera instanceof THREE.PerspectiveCamera) {
        camera.aspect = oldAspect
        camera.updateProjectionMatrix()
      }
      scene.background = oldBackground
      lights.forEach(({ light, intensity }) => {
        light.intensity = intensity
      })
      // 还原渲染器状态
      gl.setClearColor(oldClearColor, oldClearAlpha)
      renderTarget.dispose()

      return dataUrl
    }
  }))

  return null
})